// Today's pre-shift briefing for a venue: load, AI draft, edit, publish, and "Got it" reads.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';

export const BRIEFING_FIELDS = [
  { key: 'summary', label: 'Watch for today', placeholder: 'One point per line, e.g.\n- Rain likely after 6pm: be ready to move the terrace inside' },
  { key: 'bookings', label: 'Bookings', placeholder: 'e.g. 64 covers, big rush 7–8pm. Table 12: 10-person birthday at 7:30.' },
  { key: 'sections', label: 'Sections', placeholder: 'e.g. Sara: 1–10 · Tom: 11–20 · Bar: Leo' },
  { key: 'allergies', label: 'Allergies & dietary', placeholder: 'e.g. Table 4 coeliac, Table 9 nut allergy (severe)' },
  { key: 'out_of_stock', label: 'Out of stock', placeholder: 'e.g. No lamb shoulder tonight. Low on burrata.' },
  { key: 'upselling', label: 'Upselling', placeholder: 'e.g. Push the new spritz and the mezze for two.' },
  { key: 'notes', label: 'Notes', placeholder: 'Anything else the team should know.' },
] as const;
export type BriefingKey = typeof BRIEFING_FIELDS[number]['key'];

export interface Briefing extends Partial<Record<BriefingKey, string | null>> {
  id: string;
  venue_id: string;
  brief_date: string;
  status: 'draft' | 'published';
  ai_drafted: boolean;
  published_at: string | null;
  updated_at: string;
  /** Filled by the AI draft: who's on (no times) and the day's weather. */
  facts?: { team?: Array<{ name: string; part: 'allday' | 'day' | 'evening'; area: string | null }>; weather?: { label: string; min: number; max: number; rain: number } | null } | null;
}
export interface BriefingReader { user_id: string; name: string; read_at: string }

const MIGRATION = 'The briefing needs the latest database update (20261028020000_daily_briefing.sql).';
const friendly = (m: string) => (/(relation|column) "?[\w.]*venue_briefing[\w.]*"? does not exist|schema cache/i.test(m) ? MIGRATION : m);

/** Today's date in the venue's time zone. */
export const venueToday = (tz?: string | null) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export function useBriefing(venueId: string | null, userId: string | null, isManager: boolean, tz?: string | null) {
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [readers, setReaders] = useState<BriefingReader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const date = venueToday(tz);

  const load = useCallback(async () => {
    if (!venueId) return;
    setError(null);
    const { data, error: e } = await supabase.from('venue_briefings').select('*').eq('venue_id', venueId).eq('brief_date', date).maybeSingle();
    if (e) { setError(friendly(e.message)); setLoading(false); return; }
    const b = (data as Briefing | null) ?? null;
    setBriefing(b);
    if (b?.status === 'published') {
      // Staff only see their own read (RLS); managers see everyone's.
      const { data: r } = await supabase.from('venue_briefing_reads').select('user_id, read_at, users(name)').eq('briefing_id', b.id).order('read_at');
      setReaders(((r ?? []) as Array<{ user_id: string; read_at: string; users: { name: string | null } | Array<{ name: string | null }> | null }>).map((x) => {
        const u = Array.isArray(x.users) ? x.users[0] : x.users;
        return { user_id: x.user_id, read_at: x.read_at, name: u?.name ?? 'Team member' };
      }));
    } else setReaders([]);
    setLoading(false);
  }, [venueId, date]);

  useEffect(() => { setLoading(true); void load(); }, [load]);

  const draftWithAI = useCallback(async () => {
    if (!venueId) return { error: 'no venue' };
    const { data, error: e } = await supabase.functions.invoke('briefing-draft', { body: { venue_id: venueId, date } });
    const err = e ? e.message : (data as { error?: string } | null)?.error;
    if (err) return { error: friendly(err) };
    setBriefing((data as { briefing: Briefing }).briefing);
    return { error: null };
  }, [venueId, date]);

  const save = useCallback(async (fields: Partial<Record<BriefingKey, string>>, publish?: boolean) => {
    if (!venueId || !isManager) return { error: 'Managers only' };
    const clean: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(fields)) clean[k] = v?.trim() ? v.trim() : null;
    const row: Record<string, unknown> = { venue_id: venueId, brief_date: date, ...clean, updated_at: new Date().toISOString() };
    if (!briefing) { row.created_by = userId; row.status = 'draft'; }
    if (publish) { row.status = 'published'; row.published_by = userId; row.published_at = new Date().toISOString(); }
    const { data, error: e } = await supabase.from('venue_briefings').upsert(row, { onConflict: 'venue_id,brief_date' }).select('*').single();
    if (e) return { error: friendly(e.message) };
    setBriefing(data as Briefing); // publishing notifies the team (database trigger)
    return { error: null };
  }, [venueId, userId, isManager, briefing, date]);

  const unpublish = useCallback(async () => {
    if (!briefing) return { error: null };
    const { data, error: e } = await supabase.from('venue_briefings').update({ status: 'draft' }).eq('id', briefing.id).select('*').single();
    if (e) return { error: friendly(e.message) };
    setBriefing(data as Briefing); setReaders([]);
    return { error: null };
  }, [briefing]);

  const markRead = useCallback(async () => {
    if (!briefing || !userId) return { error: null };
    const { error: e } = await supabase.from('venue_briefing_reads').upsert({ briefing_id: briefing.id, user_id: userId }, { onConflict: 'briefing_id,user_id', ignoreDuplicates: true });
    if (e) return { error: friendly(e.message) };
    await load();
    return { error: null };
  }, [briefing, userId, load]);

  const hasRead = !!userId && readers.some((r) => r.user_id === userId);
  return { briefing, readers, hasRead, loading, error, date, reload: load, draftWithAI, save, unpublish, markRead };
}

